import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { toast } from "react-toastify";
import * as XLSX from 'xlsx';
import apiClient from './api/client';
import { useCurrentDomain } from "./context/DomainContext";
import { THEME } from './theme/themeColors';

// ===========================================================
// SMART SQL STATEMENT SPLITTER
// Splits on:  ;   |   GO on its own line   |   a new top-level
// DML/DDL keyword that starts a line (SELECT, INSERT, UPDATE …)
// even when the user forgets semicolons — exactly how SSMS
// handles a multi-statement editor window.
// Respects single-quoted strings, -- line comments, // block

// Keywords that legally start a new top-level SQL statement
const STMT_STARTERS = new Set([
  'SELECT','INSERT','UPDATE','DELETE','MERGE','TRUNCATE',
  'CREATE','ALTER','DROP','EXEC','EXECUTE','WITH','USE',
  'DECLARE','BEGIN','COMMIT','ROLLBACK','GRANT','REVOKE',
  'BULK','PRINT','RAISERROR','THROW','IF','WHILE',
  'ENABLE','DISABLE','DBCC','CHECKPOINT','BACKUP','RESTORE',
]);

function splitStatements(sql) {
  /* -- pass 1: tag every character with its context -------- */
  const N = sql.length;
  const ctx = new Uint8Array(N); // 0=normal 1=lineComment 2=blockComment 3=string
  let i = 0;
  while (i < N) {
    const c = sql[i], n = sql[i+1];
    if (ctx[i] === 0) {
      if (c === '-' && n === '-') {
        // line comment until \n
        let j = i;
        while (j < N && sql[j] !== '\n') { ctx[j] = 1; j++; }
        i = j;
      } else if (c === '/' && n === '*') {
        // block comment until */
        let j = i;
        while (j < N - 1 && !(sql[j] === '*' && sql[j+1] === '/')) { ctx[j] = 2; j++; }
        if (j < N - 1) { ctx[j] = 2; ctx[j+1] = 2; j += 2; }
        i = j;
      } else if (c === "'") {
        // string literal — handle '' escape
        let j = i;
        ctx[j++] = 3;
        while (j < N) {
          if (sql[j] === "'" && sql[j+1] === "'") { ctx[j] = 3; ctx[j+1] = 3; j += 2; }
          else if (sql[j] === "'") { ctx[j++] = 3; break; }
          else { ctx[j++] = 3; }
        }
        i = j;
      } else {
        i++;
      }
    } else {
      i++;
    }
  }

  /* -- pass 2: walk lines, find statement boundaries -------- */
  const lines = sql.split('\n');
  const stmts = [];
  let cur = '';
  let curOffset = 0; // character offset of the start of this line in `sql`

  const flush = () => {
    const t = cur.trim();
    if (t && !/^GO$/i.test(t)) stmts.push(t);
    cur = '';
  };

  for (let li = 0; li < lines.length; li++) {
    const line = lines[li];
    const lineStart = curOffset;
    const lineEnd   = lineStart + line.length;
    curOffset = lineEnd + 1; // +1 for the \n we split on

    // Is this entire line inside a comment or string?
    const allEscaped = lineStart < N
      ? [...Array(Math.min(line.length, N - lineStart))].every((_, k) => ctx[lineStart + k] !== 0)
      : false;

    // Does this line start with a statement-starter keyword (in normal context)?
    let lineStartsStmt = false;
    if (!allEscaped && line.trim().length > 0) {
      // find first non-whitespace char offset inside the line that is in ctx=0
      for (let k = 0; k < line.length; k++) {
        const absK = lineStart + k;
        if (absK >= N) break;
        if (/\s/.test(line[k])) continue;
        if (ctx[absK] !== 0) break; // inside comment or string → not a stmt boundary
        // extract the word starting at k
        let wEnd = k;
        while (wEnd < line.length && /[\w]/.test(line[wEnd])) wEnd++;
        const word = line.slice(k, wEnd).toUpperCase();
        if (['SET', 'FROM', 'JOIN', 'WHERE', 'ORDER', 'GROUP', 'HAVING'].includes(word)) {
          lineStartsStmt = false;
        } else {
          lineStartsStmt = STMT_STARTERS.has(word);
        }
        lineStartsStmt = STMT_STARTERS.has(word);
        break;
      }
    }

    // Check if the line (in normal context) contains a ; boundary
    let pendingChunk = '';
    for (let k = 0; k < line.length; k++) {
      const absK = lineStart + k;
      const ch = line[k];
      if (absK < N && ctx[absK] === 0 && ch === ';') {
        cur += pendingChunk;
        pendingChunk = '';
        flush();
      } else {
        pendingChunk += ch;
      }
    }

    // If this line starts a new statement AND cur already has content → split before this line
    if (lineStartsStmt && cur.trim().length > 0) {
      flush();
      cur = pendingChunk + '\n';
    } else {
      cur += pendingChunk + '\n';
    }
  }

  // flush remaining content — also split on GO
  const rem = cur.split(/^GO$/im).map(s => s.trim()).filter(Boolean);
  stmts.push(...rem);

  return stmts.filter(s => s && s.trim().length > 3); 
}

// ===========================================================
// TABLE NAME EXTRACTOR
// ===========================================================
const extractTableName = sql => {
  const c = sql.trim().toUpperCase();
  const fi = c.indexOf('FROM');
  if (fi < 0) return null;
  const af = c.slice(fi + 4).trim();
  const stops = [' WHERE', ' JOIN', ' GROUP', ' ORDER', ';', ' LIMIT', '\n', ' HAVING', ' INNER', ' LEFT', ' RIGHT', ' CROSS'];
  const ei = Math.min(...stops.map(k => af.indexOf(k)).filter(i => i > 0).concat([af.length]));
  return af.slice(0, ei).trim().split(/\s+/)[0].replace(/^[\[`"]+/, '').replace(/[\]`"]+$/, '') || null;
};

const hasWhereClause = (sql) => {
  if (!sql) return false;
  const upper = sql.toUpperCase();
  return /\bWHERE\b/i.test(upper) &&
         !/\bWHERE\b\s*(?:;|$)/i.test(upper); 
};

const isDeleteStatement = (sql) => {
  const upper = sql.trim().toUpperCase();
  return upper.startsWith('DELETE');
};

// ===========================================================
// TAB FACTORY
// ===========================================================
let _tabN = 1;
const newTab = (q = '', savedFileName = '') => ({
  id: `tab_${Date.now()}_${Math.random().toString(36).slice(2)}`,
  name: savedFileName ? savedFileName.replace(/\.sql$/, '') : `SQLQuery${_tabN++}`,
  query: q,
  savedFileName: savedFileName,
  results: [],
  messages: [],
  loading: false,
  stats: null,
});

// ===========================================================
// QUERY ANALYZER — MAIN COMPONENT
// ===========================================================
const QueryAnalyzer = () => {
  const { domain } = useCurrentDomain();

  const [tabs, setTabs] = useState(() => [newTab()]);
  const [activeTabId, setActiveTabId] = useState(null); // null → falls back to tabs[0]
  const [editorPct, setEditorPct] = useState(42);
  const [activeResultPane, setActiveResultPane] = useState('results');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteSql, setPendingDeleteSql] = useState(null);
  const [pendingTabId, setPendingTabId] = useState(null);
  const [showWhereMissing, setShowWhereMissing] = useState(false);
  const [missingSql, setMissingSql] = useState(null);
    // Query Library
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveFileName, setSaveFileName] = useState('');
  const [libraryFiles, setLibraryFiles] = useState([]);
  const [showLibraryMenu, setShowLibraryMenu] = useState(false);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [showDeleteFileConfirm, setShowDeleteFileConfirm] = useState(false);
  const [pendingDeleteFile, setPendingDeleteFile] = useState(null);
  const editorPctRef = useRef(42);
  const tabsRef = useRef(tabs);
  const containerRef = useRef(null);
  const dividerRef = useRef(null);
  const abortRefs = useRef({});     // tabId → AbortController
  const textareaRefs = useRef({});  // tabId → <textarea> DOM node

  useEffect(() => { editorPctRef.current = editorPct; }, [editorPct]);
  useEffect(() => { tabsRef.current = tabs; }, [tabs]);

  const activeTab = tabs.find(t => t.id === activeTabId) || tabs[0];

  /* -- update a single tab -- */
  const updateTab = useCallback((id, patch) =>
    setTabs(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t)),
    []
  );

  /* -- MAIN DIVIDER DRAG -- */
  useEffect(() => {
    const divider = dividerRef.current, container = containerRef.current;
    if (!divider || !container) return;
    let drag = false, sY = 0, sPx = 0, cH = 0;
    const start = e => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault(); drag = true; cH = container.offsetHeight;
      sY = e.clientY; sPx = (editorPctRef.current / 100) * cH;
      document.body.style.userSelect = 'none'; document.body.style.cursor = 'row-resize';
    };
    const move = e => {
      if (!drag) return; e.preventDefault();
      const np = Math.max(80, Math.min(cH - 100, sPx + (e.clientY - sY)));
      setEditorPct(Math.round((np / cH) * 100));
    };
    const stop = () => { drag = false; document.body.style.userSelect = ''; document.body.style.cursor = ''; };
    divider.addEventListener('pointerdown', start);
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', stop);
    return () => {
      divider.removeEventListener('pointerdown', start);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
    };
  }, []);

  const handleExecute = useCallback(async (tabId, preSelectedText = null, bypassSafety = false) => {
    const tab = tabsRef.current.find(t => t.id === tabId);
    if (!tab) return;

    // SSMS behaviour: if text is selected (from keyboard shortcut OR button click),
    // execute ONLY the selection. Check the textarea for an active selection
    // whenever preSelectedText is not explicitly provided.
    let rawQuery = tab.query;
    if (preSelectedText && preSelectedText.trim()) {
      // Explicit selection passed from keydown handler
      rawQuery = preSelectedText;
    } else {
      // Button click or F5 — check if textarea has an active selection
      const ta = textareaRefs.current[tabId];
      if (ta) {
        const sel = ta.value.substring(ta.selectionStart, ta.selectionEnd).trim();
        if (sel) rawQuery = sel; // run selection only, silently — just like SSMS
      }
    }

    const statements = splitStatements(rawQuery);
    if (!statements.length) { toast.error('No SQL statements found'); return; }

    // === SAFETY CHECKS ===
    if (!bypassSafety) {
      for (let i = 0; i < statements.length; i++) {
        const sql = statements[i].trim();
        const upperSql = sql.toUpperCase();

        if ((upperSql.startsWith('DELETE') || upperSql.startsWith('UPDATE') || upperSql.startsWith('INSERT')) && 
            !hasWhereClause(sql)) {
          
          setMissingSql(sql);
          setShowWhereMissing(true);
          return; 
        }

        // Show confirmation only for valid DELETE
        if (isDeleteStatement(sql)) {
          setPendingDeleteSql(sql);
          setPendingTabId(tabId);
          setShowDeleteConfirm(true);
          return;
        }
      }
    }

    // NEW: Smart Decision - If query contains DECLARE or SET, run as ONE batch
    const isVariableQuery = rawQuery.toUpperCase().includes('DECLARE') || 
                           rawQuery.toUpperCase().includes('SET @');

    if (isVariableQuery) {
      // Run as single batch for variable scope
      if (abortRefs.current[tabId]) abortRefs.current[tabId].abort();
      const ac = new AbortController();
      abortRefs.current[tabId] = ac;

      updateTab(tabId, { loading: true, results: [], messages: [], stats: null });
      setActiveResultPane('results');

      const messages = [];
      const overallStart = performance.now();
      const domain = sessionStorage?.getItem('selectedDomain');
      const domainID = sessionStorage?.getItem('selectedDomainId');
      const role = sessionStorage?.getItem('userRoleId');
      const UserId = sessionStorage?.getItem('userId');

      const t0 = performance.now();
      try {
        const res = await apiClient.post('/api/query/execute',
          { domain, domainID, sql: rawQuery, frontendRole: role, UserId },
          { signal: ac.signal }
        );
        const secs = ((performance.now() - t0) / 1000).toFixed(2);

        if (res.data.success !== false) {
          const rows = res.data.rowCount ?? 0;
          messages.push({ type: 'info', text: `Executed successfully (${secs}s)` });

          const resultItem = {
            index: 0,
            success: true,
            data: res.data,
            executionTime: secs,
            tableName: null,
            sql: rawQuery
          };

          updateTab(tabId, {
            loading: false,
            results: [resultItem],
            messages,
            stats: { statements: 1, rows: rows, time: secs, failed: 0 }
          });

          toast.success(`Query completed · ${rows} rows · ${secs}s`);
        } else {
          // error handling for single batch
          messages.push({ type: 'error', text: res.data.message || 'Query failed' });
          updateTab(tabId, {
            loading: false,
            results: [{ index: 0, success: false, error: res.data.message || 'Query failed', executionTime: secs, sql: rawQuery }],
            messages,
            stats: { statements: 1, rows: 0, time: secs, failed: 1 }
          });
          setActiveResultPane('messages');
          toast.error('Query failed — see Messages tab');
        }
      } catch (err) {
        // error handling...
        const secs = ((performance.now() - t0) / 1000).toFixed(2);
        let msg = err.response?.data?.message || err.message || 'Request failed';
        messages.push({ type: 'error', text: msg });
        updateTab(tabId, {
          loading: false,
          results: [{ index: 0, success: false, error: msg, executionTime: secs, sql: rawQuery }],
          messages,
          stats: { statements: 1, rows: 0, time: secs, failed: 1 }
        });
        setActiveResultPane('messages');
        toast.error('Query failed — see Messages tab');
      }
    } else {
      // Original behavior for normal multi-SELECT queries
      if (abortRefs.current[tabId]) abortRefs.current[tabId].abort();
      const ac = new AbortController();
      abortRefs.current[tabId] = ac;

      updateTab(tabId, { loading: true, results: [], messages: [], stats: null });
      setActiveResultPane('results');

      const messages = [];
      const overallStart = performance.now();
      const domain = sessionStorage?.getItem('selectedDomain');
      const domainID = sessionStorage?.getItem('selectedDomainId');
      const role = sessionStorage?.getItem('userRoleId');
      const UserId = sessionStorage?.getItem('userId');

      const promises = statements.map(async (sql, idx) => {
        const t0 = performance.now();
        try {
          const res = await apiClient.post('/api/query/execute',
            { domain,domainID, sql, frontendRole: role, UserId },
            { signal: ac.signal }
          );
          const secs = ((performance.now() - t0) / 1000).toFixed(2);
          if (res.data.success !== false) {
            const rows = res.data.rowCount ?? 0;
            messages.push({ type: 'info', text: `Statement ${idx + 1}: ${rows} row(s) returned (${secs}s)` });
            return { index: idx, success: true, data: res.data, executionTime: secs, tableName: extractTableName(sql), sql };
          } else {
            messages.push({ type: 'error', text: `Statement ${idx + 1} failed: ${res.data.message || 'Unknown error'}` });
            return { index: idx, success: false, error: res.data.message || 'Query failed', executionTime: secs, sql };
          }
        } catch (err) {
          const secs = ((performance.now() - t0) / 1000).toFixed(2);
          if (err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
            messages.push({ type: 'warn', text: `Statement ${idx + 1}: Cancelled by user` });
            return { index: idx, success: false, error: 'Cancelled', executionTime: '—', sql };
          }
          const msg = err.response?.data?.message || err.message || 'Request failed';
          messages.push({ type: 'error', text: `Statement ${idx + 1} error: ${msg}` });
          return { index: idx, success: false, error: msg, executionTime: secs, sql };
        }
      });

      const settled = await Promise.allSettled(promises);
      const results = settled
        .map((p, i) => p.value || { index: i, success: false, error: 'Unexpected error', executionTime: '—' })
        .sort((a, b) => a.index - b.index);

      const totalSecs = ((performance.now() - overallStart) / 1000).toFixed(2);
      const totalRows = results.reduce((s, r) => s + (r.success && r.data?.rowCount ? r.data.rowCount : 0), 0);
      const failed = results.filter(r => !r.success).length;

      messages.push({
        type: failed ? 'error' : 'info',
        text: `\n---- Completed: ${results.length} statement(s) in ${totalSecs}s · ${totalRows} total row(s)${failed ? ` · ${failed} failed` : ''} ----`
      });

      updateTab(tabId, {
        loading: false, results, messages,
        stats: { statements: results.length, rows: totalRows, time: totalSecs, failed }
      });

      if (failed) {
        setActiveResultPane('messages');
        toast.error(`${failed} statement(s) failed — see Messages tab`);
      } else {
        toast.success(`${results.length} statement(s) · ${totalRows} rows · ${totalSecs}s`);
      }
    }
  }, [updateTab]);

  /* -- CANCEL -- */
  const handleCancel = useCallback((tabId) => {
    if (abortRefs.current[tabId]) {
      abortRefs.current[tabId].abort();
      updateTab(tabId, { loading: false });
      toast('Query cancelled', { icon: '⛔' });
    }
  }, [updateTab]);

  /* -- EXPORT -- */
  const handleExport = useCallback((columns, rows, fileName) => {
    try {
      const MAX = 32767;
      const san = v => { if (v == null) return ''; const s = String(v); return s.length > MAX ? s.slice(0, MAX - 3) + '...' : s; };
      const wsData = [columns.map(san), ...rows.map(r => r.map(san))];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws['!cols'] = columns.map((c, ci) => ({
        wch: Math.min(Math.max(...[c, ...rows.map(r => String(r[ci] ?? ''))].map(v => Math.min(String(v).length, 100))) + 2, 60)
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Results');
      XLSX.writeFile(wb, fileName || `query_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success('Excel downloaded');
    } catch (e) { toast.error('Export failed: ' + e.message); }
  }, []);

    /* -- QUERY LIBRARY HELPERS -- */
  const fetchLibrary = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/query/library/list', {
        headers: {
          'X-User-Name': sessionStorage.getItem('username') ,
          'X-User-Id': sessionStorage.getItem('userId') 
        }
      });
      if (res.data.success) setLibraryFiles(res.data.files || []);
    } catch (e) {
      toast.error('Failed to load library');
    }
  }, []);

  const saveToLibrary = useCallback(async () => {
    const currentTab = tabsRef.current.find(t => t.id === activeTabId) || tabs[0];
    if (!currentTab.query.trim()) {
      toast.error('No query to save');
      return;
    }

    if (currentTab.savedFileName) {
      try {
        const res = await apiClient.post('/api/query/library/save', {
          fileName: currentTab.savedFileName.replace(/\.sql$/, ''),
          sql: currentTab.query,
          domain: domain,
          isUpdate: true
        }, {
          headers: {
            'X-User-Name': sessionStorage.getItem('username') ,
            'X-User-Id': sessionStorage.getItem('userId') 
          }
        });

        if (res.data.success) {
          toast.success(`Query "${currentTab.name}" updated successfully!`);
          fetchLibrary();
        } else {
          toast.error(res.data.message || 'Update failed');
        }
      } catch (e) {
        toast.error(e.response?.data?.message || 'Update failed');
      }
      return;
    }

    setSaveFileName('');
    setShowSaveModal(true);
  }, [activeTabId, tabs, domain, fetchLibrary]);

  const loadQuery = useCallback(async (fileName) => {
    try {
      const res = await apiClient.get(`/api/query/library/load/${fileName}`, {
        headers: {
          'X-User-Name': sessionStorage.getItem('username'),
          'X-User-Id': sessionStorage.getItem('userId')
        }
      });
      if (res.data.success) {
        const newTabObj = newTab(res.data.content, fileName);
        
        setTabs(prev => [...prev, newTabObj]);
        setActiveTabId(newTabObj.id);
        setShowLibraryMenu(false);
      } else {
        toast.error(res.data.message || 'Load failed');
      }
    } catch (e) {
      toast.error('Failed to load query');
    }
  }, [setTabs, setActiveTabId, setShowLibraryMenu]);

    const deleteQuery = useCallback(async (fileName) => {
      setPendingDeleteFile(fileName);
      setShowDeleteFileConfirm(true);
    }, []);

    const deleteAllLibrary = useCallback(async () => {
      setShowDeleteAllConfirm(true);
    }, []);

  // Load library on mount
  useEffect(() => {
    fetchLibrary();
  }, [fetchLibrary]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showLibraryMenu && !e.target.closest('.library-menu') && 
          !e.target.closest('button[aria-label="Query Library"]')) { 
        setShowLibraryMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLibraryMenu]);

  /* -- TAB MANAGEMENT -- */
  const addTab = useCallback(() => {
    const t = newTab();
    setTabs(prev => [...prev, t]);
    setActiveTabId(t.id);
  }, []);

  const closeTab = useCallback((e, id) => {
    e.stopPropagation();
    setTabs(prev => {
      if (prev.length === 1) {
        const fresh = newTab();
        setActiveTabId(fresh.id);
        return [fresh];
      }
      const next = prev.filter(t => t.id !== id);
      setActiveTabId(cur => {
        if (cur !== id) return cur;
        const idx = prev.findIndex(t => t.id === id);
        return (next[idx] || next[idx - 1] || next[0]).id;
      });
      return next;
    });
    if (abortRefs.current[id]) abortRefs.current[id].abort();
    delete abortRefs.current[id];
    delete textareaRefs.current[id];
  }, []);

  /* New tab: use Alt+T in the editor or click + in the tab bar */

  const tab = activeTab;

  return (
    <div style={{ height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column', background: '#1e1e1e', overflow: 'hidden', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

      {/* -- TAB BAR -- */}
      <TabBar 
        tabs={tabs} 
        activeTabId={tab.id} 
        setActiveTabId={setActiveTabId} 
        onAdd={addTab} 
        onClose={closeTab}
        onSaveClick={saveToLibrary}  
        activeTab={tab}
      />
      {/* -- TOOLBAR -- */}
      <Toolbar
        tab={tab} domain={domain}
        onExecute={() => handleExecute(tab.id)}
        onCancel={() => handleCancel(tab.id)}
        onReset={() => updateTab(tab.id, { query: '', results: [], messages: [], stats: null })}
        onSave={saveToLibrary}
        libraryFiles={libraryFiles}
        onLoadQuery={loadQuery}
        deleteQuery={deleteQuery}
        deleteAllLibrary={deleteAllLibrary}
        fetchLibrary={fetchLibrary}
        showLibraryMenu={showLibraryMenu}
        setShowLibraryMenu={setShowLibraryMenu}
        canAct={!!tab.query.trim()}
      />

      {/* -- BODY: EDITOR + RESULTS -- */}
      <div ref={containerRef} style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>

        {/* EDITOR */}
        <div style={{ height: `${editorPct}%`, minHeight: 80, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <Editor
            key={tab.id}
            tabId={tab.id}
            value={tab.query}
            onChange={q => updateTab(tab.id, { query: q })}
            onExecute={() => handleExecute(tab.id)}
            onExecuteSelected={(sel) => handleExecute(tab.id, sel)}
            onNewTab={addTab}
            textareaRefs={textareaRefs}
          />
        </div>

        {/* MAIN DIVIDER */}
        <div
          ref={dividerRef}
          style={{ height: 5, background: '#2d2d2d', cursor: 'row-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          onMouseEnter={e => e.currentTarget.style.background = '#0078d4'}
          onMouseLeave={e => e.currentTarget.style.background = '#2d2d2d'}
        >
          <div style={{ width: 60, height: 2, background: '#555', borderRadius: 99 }} />
        </div>

        {/* RESULTS */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <ResultsPane
            tab={tab}
            activePane={activeResultPane}
            setActivePane={setActiveResultPane}
            onExport={handleExport}
          />
        </div>
      </div>

      {/* -- STATUS BAR -- */}
      <StatusBar tab={tab} domain={domain} textareaRefs={textareaRefs} />

      {/* SAVE MODAL */}
      {showSaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.85)',
          zIndex: 10002,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#1e1e1e',
            padding: '20px',
            borderRadius: '8px',
            width: '460px',
            maxWidth: '90%',
            border: '1px solid #0078d4'
          }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#fff', fontSize: '16px' }}>Save to Query Library</h3>
            
            <input
              type="text"
              value={saveFileName}
              onChange={e => setSaveFileName(e.target.value)}
              placeholder="Filename"
              style={{
                width: '100%',
                padding: '9px 12px',
                background: '#2d2d2d',
                border: '1px solid #555',
                color: '#fff',
                borderRadius: '4px',
                marginBottom: '12px',
                fontSize: '13.5px'
              }}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && saveToLibrary()}
            />

            <div style={{ 
              background: '#252526', 
              padding: '10px', 
              borderRadius: '4px',
              maxHeight: '160px',
              overflow: 'auto',
              fontFamily: 'Consolas, monospace',
              fontSize: '12px',
              color: '#aaa',
              marginBottom: '14px',
              border: '1px solid #444'
            }}>
              {(tabsRef.current.find(t => t.id === activeTabId) || tabs[0]).query?.slice(0, 450) || '-- empty --'}
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowSaveModal(false); setSaveFileName(''); }}
                style={{ padding: '2px 10px', background: '#333', color: '#ccc', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button 
                onClick={async () => {
                  if (!saveFileName.trim()) {
                    toast.error('Filename is required');
                    return;
                  }

                  const currentTab = tabsRef.current.find(t => t.id === activeTabId) || tabs[0];

                  try {
                    const res = await apiClient.post('/api/query/library/save', {
                      fileName: saveFileName.trim(),
                      sql: currentTab.query,
                      domain: domain,
                      isUpdate: false
                    }, {
                      headers: {
                        'X-User-Name': sessionStorage.getItem('username'),
                        'X-User-Id': sessionStorage.getItem('userId') 
                      }
                    });

                    if (res.data.success) {
                      updateTab(activeTabId || tabs[0].id, {
                        name: saveFileName.trim(),
                        savedFileName: saveFileName.trim() + '.sql'
                      });
                      toast.success('Query saved successfully!');
                      setShowSaveModal(false);
                      setSaveFileName('');
                      fetchLibrary();
                    } else {
                      toast.error(res.data.message || 'Save failed');
                    }
                  } catch (e) {
                    toast.error(e.response?.data?.message || 'Save failed');
                  }
                }}
style={{ padding: '2px 10px', background: THEME.primary, color: THEME.onPrimary, border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}>                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE ALL LIBRARY CONFIRMATION */}
      {showDeleteAllConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.75)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#1e1e1e',
            padding: '18px',
            borderRadius: '8px',
            width: '420px',
            maxWidth: '90%',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            border: '1px solid #f87171'
          }}>
            <p style={{ marginBottom: '10px', fontSize: '14px', color: '#f87171', fontWeight: '600' }}>
              Delete Entire Query Library?
            </p>
            <p style={{ marginBottom: '16px', fontSize: '12.5px', lineHeight: '1.4', color: '#ddd' }}>
              This will permanently delete ALL your saved queries.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteAllConfirm(false)}
                style={{ padding: '6px 18px', border: '1px solid #555', borderRadius: '6px', background: '#333', color: '#ccc', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setShowDeleteAllConfirm(false);
                  try {
                    const res = await apiClient.delete('/api/query/library/delete-all', {
                      headers: {
                        'X-User-Name': sessionStorage.getItem('username'),
                        'X-User-Id': sessionStorage.getItem('userId')
                      }
                    });
                    if (res.data.success) {
                      toast.success('Library deleted successfully!');
                      setLibraryFiles([]);
                    } else {
                      toast.error(res.data.message || 'Failed to delete library');
                    }
                  } catch (e) {
                    toast.error('Failed to delete library');
                  }
                }}
                style={{ padding: '6px 18px', border: 'none', borderRadius: '6px', background: THEME.error, color: THEME.onError, cursor: 'pointer', fontWeight: '600' }}
              >
                Delete All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SINGLE FILE DELETE CONFIRMATION */}
      {showDeleteFileConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.75)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#1e1e1e',
            padding: '18px',
            borderRadius: '8px',
            width: '400px',
            maxWidth: '90%',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            border: '1px solid #f87171'
          }}>
            <p style={{ marginBottom: '10px', fontSize: '14px', color: '#f87171', fontWeight: '600' }}>
              Delete Query?
            </p>
            <p style={{ marginBottom: '16px', fontSize: '13px', color: '#ddd' }}>
              Are you sure you want to delete <strong>{pendingDeleteFile}</strong>?
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDeleteFileConfirm(false);
                  setPendingDeleteFile(null);
                }}
                style={{ padding: '6px 18px', border: '1px solid #555', borderRadius: '6px', background: '#333', color: '#ccc', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const fileToDelete = pendingDeleteFile;
                  setShowDeleteFileConfirm(false);
                  setPendingDeleteFile(null);
                  try {
                    const res = await apiClient.delete(`/api/query/library/delete/${fileToDelete}`, {
                      headers: {
                        'X-User-Name': sessionStorage.getItem('username') ,
                        'X-User-Id': sessionStorage.getItem('userId') 
                      }
                    });
                    if (res.data.success) {
                      toast.success('Deleted successfully!');
                      fetchLibrary();
                    } else {
                      toast.error(res.data.message || 'Delete failed');
                    }
                  } catch (e) {
                    toast.error('Delete failed');
                  }
                }}
                style={{ padding: '6px 18px', border: 'none', borderRadius: '6px', background: '#b91c1c', color: 'white', cursor: 'pointer', fontWeight: '600' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.75)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#1e1e1e',
            padding: '18px',
            borderRadius: '8px',
            width: '380px',
            maxWidth: '90%',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            border: '1px solid #f87171'
          }}>
            <p style={{
              marginBottom: '10px',
              fontSize: '14px',
              color: '#f87171',
              fontWeight: '600'
            }}>
              DELETE Statement Detected
            </p>
            
            <p style={{
              marginBottom: '16px',
              fontSize: '12.5px',
              lineHeight: '1.4',
              color: '#ddd'
            }}>
              This action will permanently delete data and cannot be undone.<br />
              Please check before proceeding.
            </p>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setPendingDeleteSql(null);
                  setPendingTabId(null);
                }}
                style={{
                  padding: '6px 18px',
                  border: '1px solid #555',
                  borderRadius: '6px',
                  background: '#333',
                  color: '#ccc',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Cancel
              </button>

              <button
                onClick={async () => {
                  const tabIdToRun = pendingTabId;
                  setShowDeleteConfirm(false);
                  setPendingDeleteSql(null);
                  setPendingTabId(null);

                  if (tabIdToRun) {
                    const tab = tabsRef.current.find(t => t.id === tabIdToRun);
                    if (!tab) return;
                    if (abortRefs.current[tabIdToRun]) abortRefs.current[tabIdToRun].abort();
                    const ac = new AbortController();
                    abortRefs.current[tabIdToRun] = ac;
                    await handleExecute(tabIdToRun, null, true); 
                  }
                }}
                style={{
                  padding: '6px 18px',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#b91c1c',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '12px'
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}      

      {/* WHERE CLAUSE MISSING MODAL */}
      {showWhereMissing && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.75)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#1e1e1e',
            padding: '18px',
            borderRadius: '8px',
            width: '400px',
            maxWidth: '90%',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            border: '1px solid #f87171'
          }}>
            <p style={{
              marginBottom: '10px',
              fontSize: '14px',
              color: '#f87171',
              fontWeight: '600'
            }}>
              WHERE Clause Missing
            </p>
            
            <p style={{
              marginBottom: '16px',
              fontSize: '12.5px',
              lineHeight: '1.4',
              color: '#ddd'
            }}>
              The following query cannot be executed because it is missing a WHERE clause:<br /><br />
              <strong style={{ color: '#fbbf24', wordBreak: 'break-all' }}>{missingSql}</strong>
            </p>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowWhereMissing(false);
                  setMissingSql(null);
                }}
                style={{
                  padding: '6px 18px',
                  border: '1px solid #555',
                  borderRadius: '6px',
                  background: '#333',
                  color: '#ccc',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes qs-spin { to { transform: rotate(360deg); } }
        .qs-tab { transition: background 0.1s; }
        .qs-tab:hover:not(.qs-active) { background: #2a2d2e !important; color: #aaa !important; }
        .qs-tbtn:hover:not(:disabled) { background: #3a3a3a !important; }
        .qs-row:hover > td { background: #2a2d2e !important; }
        .qs-hdr:hover { background: #2d3748 !important; }
        ::-webkit-scrollbar { width: 9px; height: 9px; }
        ::-webkit-scrollbar-track { background: #1e1e1e; }
        ::-webkit-scrollbar-thumb { background: #424242; border-radius: 5px; border: 2px solid #1e1e1e; }
        ::-webkit-scrollbar-thumb:hover { background: #555; }
        ::-webkit-scrollbar-corner { background: #1e1e1e; }
        .qs-textarea::placeholder { color: #3d4451 !important; }
        [contenteditable][data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: #3d4451;
          pointer-events: none;
          white-space: pre;
          display: block;
        }
      `}</style>
    </div>
  );
};

// ===========================================================
// TAB BAR
// ===========================================================
const TabBar = ({ tabs, activeTabId, setActiveTabId, onAdd, onClose, onSaveClick, activeTab }) => (
  <div style={{ display: 'flex', alignItems: 'flex-end', background: '#252526', borderBottom: '1px solid #1a1a1a', paddingTop: 3, paddingLeft: 4, overflowX: 'auto', flexShrink: 0, minHeight: 34 }}>
    {tabs.map(t => {
      const isActive = t.id === activeTabId;
      return (
        <div
          key={t.id}
          className={`qs-tab${isActive ? ' qs-active' : ''}`}
          onClick={() => setActiveTabId(t.id)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '5px 12px 5px 10px',
            cursor: 'pointer', userSelect: 'none',
            background: isActive ? '#1e1e1e' : '#2d2d2d',
            color: isActive ? '#cccccc' : '#777777',
            fontSize: 12, whiteSpace: 'nowrap',
            borderTop: isActive ? '2px solid #0078d4' : '2px solid transparent',
            borderRight: '1px solid #1a1a1a',
            marginRight: 1, flexShrink: 0,
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" opacity={0.7}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
          </svg>
          {t.name}
          {t.loading && <span style={{ width: 7, height: 7, border: '1.5px solid #0078d4', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'qs-spin 0.7s linear infinite' }} />}

          {/* Save Icon next to tab name - Smart Save */}
          {isActive && (
            <span 
              onClick={(e) => { 
                e.stopPropagation(); 
                onSaveClick();           // This now handles both Update + New
              }}
              title={t.savedFileName ? "Save changes (Update)" : "Save Query"}
              style={{ 
                marginLeft: 4, 
                color: THEME.primary, 
                cursor: 'pointer',
                fontSize: '14px',
                padding: '0 4px'
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#4ade80'}
              onMouseLeave={e => e.currentTarget.style.color = THEME.primary}
            >
              💾
            </span>
          )}

          <span
            onClick={e => onClose(e, t.id)}
            style={{ marginLeft: 2, color: '#555', fontSize: 13, lineHeight: 1, cursor: 'pointer', padding: '0 2px', borderRadius: 3, transition: 'all 0.1s' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#ccc'; e.currentTarget.style.background = '#555'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#555'; e.currentTarget.style.background = 'transparent'; }}
          >×</span>
        </div>
      );
    })}
    <button
      onClick={onAdd}
      title="New Query Tab (Alt+T)"
      style={{ padding: '3px 10px 5px', background: 'transparent', border: 'none', color: '#666', fontSize: 18, cursor: 'pointer', lineHeight: 1, alignSelf: 'center', transition: 'color 0.1s' }}
      onMouseEnter={e => e.currentTarget.style.color = '#ccc'}
      onMouseLeave={e => e.currentTarget.style.color = '#666'}
    >+</button>
  </div>
);

// ===========================================================
// TOOLBAR
// ===========================================================
const Toolbar = ({ tab, domain, onExecute, onCancel, onReset, onSave, libraryFiles, onLoadQuery, deleteQuery, deleteAllLibrary, fetchLibrary, showLibraryMenu, setShowLibraryMenu, canAct: propCanAct }) => {
  const base = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 11px', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'background 0.12s' };
  const canAct = propCanAct !== undefined ? propCanAct : !!tab.query.trim();

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '4px 10px', background: '#2d2d2d', borderBottom: '1px solid #1a1a1a', flexShrink: 0 }}>

      {/* Execute / Cancel */}
      {!tab.loading ? (
        <button className="qs-tbtn" style={{ ...base, background: '#0e7a0e', color: '#fff', opacity: canAct ? 1 : 0.45 }}
          onClick={onExecute} disabled={!canAct} title="Execute selection if selected, otherwise all (F5 · Ctrl+Enter)">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21" /></svg>
          Execute
        </button>
      ) : (
        <button className="qs-tbtn" style={{ ...base, background: '#8b1a1a', color: '#fff' }}
          onClick={onCancel} title="Cancel Query (Esc)">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><rect x="4" y="4" width="16" height="16" rx="2" /></svg>
          Cancel
        </button>
      )}

      {/* Clear */}
      <button className="qs-tbtn"
        style={{ ...base, background: 'transparent', color: '#777', opacity: canAct || tab.results.length ? 1 : 0.4 }}
        onClick={onReset} disabled={!canAct && !tab.results.length}
        title="Clear editor and results (Ctrl+Shift+D)">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-3" />
        </svg>
        Clear
      </button>

      <div style={{ flex: 1 }} />

      <span style={{ fontSize: 10, color: '#444', marginRight: 10 }}>F5 / Ctrl+Enter: Execute · Ctrl+/: Comment · Alt+T: New Tab</span>

      {/* === QUERY LIBRARY DROPDOWN === */}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          aria-label="Query Library"
          onClick={() => { fetchLibrary(); setShowLibraryMenu(!showLibraryMenu); }}
          style={{
            background: '#1e1e1e',
            color: '#ccc',
            border: '1px solid #555',
            borderRadius: '4px',
            padding: '5px 10px',
            fontSize: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}
        >
          Query Library ▼
        </button>

        {/* Dropdown Menu */}
        {showLibraryMenu && (
          <div 
            className="library-menu"
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              background: '#252526',
              border: '1px solid #555',
              borderRadius: '4px',
              width: '260px',
              maxHeight: '50vh',
              overflow: 'auto',
              zIndex: 9999,
              boxShadow: '0 4px 15px rgba(0,0,0,0.6)',
              marginTop: '4px',
              padding: '4px 0'
            }}
          >

            {libraryFiles.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#777', fontSize: '13px' }}>
                No saved queries
              </div>
            ) : (
              libraryFiles.map((file, i) => (
                <div 
                  key={i} 
                  style={{
                    padding: '8px 12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    color: '#ddd',
                    fontSize: '13px',
                    borderBottom: i < libraryFiles.length - 1 ? '1px solid #333' : 'none'
                  }}
                  onClick={() => onLoadQuery(file)}
                  onMouseEnter={e => { e.currentTarget.style.background = '#2a2d2e'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span>{file.replace(/\.sql$/, '')}</span>
                  <button 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      deleteQuery(file); 
                    }}
                    style={{ 
                      color: '#ff6b6b', 
                      background: 'none', 
                      border: 'none', 
                      cursor: 'pointer',
                      fontSize: '16px',
                      padding: '0 4px',
                      lineHeight: 1
                    }}
                    title="Delete the query"
                  >
                    ×
                  </button>
                </div>
              ))
            )}

            {/* Delete All Button */}
            <div style={{ padding: '8px 12px', borderTop: '1px solid #333', marginTop: '4px' }}>
              <button 
                onClick={deleteAllLibrary}
                style={{ 
                  color: '#ff6b6b', 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '12px',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left',
                  padding: '4px 0'
                }}
              >
                Delete All Queries
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};


// ===========================================================
// SQL SYNTAX HIGHLIGHTER
// Minimal, overlay-safe tokeniser. Only produces spans — no
// nested tags, no attribute values — so innerHTML is safe.
// Characters are never modified; only wrapped in colour spans.
// This keeps the overlay pixel-identical to the textarea text.
// ===========================================================
function sqlHighlight(code) {
  // We walk char-by-char and emit HTML. The output length in
  // RENDERED characters must equal code.length exactly so the
  // transparent textarea caret stays perfectly aligned.
  const esc = s => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

  const KW = new Set('SELECT FROM WHERE AND OR NOT IN IS NULL AS ON JOIN INNER LEFT RIGHT FULL OUTER CROSS UNION ALL DISTINCT TOP LIMIT OFFSET GROUP BY ORDER HAVING INTO VALUES INSERT UPDATE DELETE SET CREATE ALTER DROP TABLE INDEX VIEW DATABASE SCHEMA IF EXISTS THEN ELSE END CASE WHEN WITH EXEC EXECUTE DECLARE BEGIN COMMIT ROLLBACK TRANSACTION PRIMARY KEY FOREIGN REFERENCES CONSTRAINT DEFAULT UNIQUE CHECK IDENTITY GO USE TRUNCATE MERGE OUTPUT RETURNS RETURN PROCEDURE FUNCTION TRIGGER WHILE FOR BREAK CONTINUE PRINT NOLOCK BETWEEN LIKE EXISTS ANY SOME OVER PARTITION NULLS ASC DESC PIVOT APPLY'.split(' '));
  const FN = new Set('COUNT SUM AVG MIN MAX COALESCE ISNULL NULLIF CAST CONVERT GETDATE GETUTCDATE SYSDATETIME NOW DATEADD DATEDIFF DATENAME DATEPART FORMAT TRIM LTRIM RTRIM UPPER LOWER LEN LENGTH SUBSTRING SUBSTR CHARINDEX REPLACE STUFF CONCAT STRING_AGG ROW_NUMBER RANK DENSE_RANK NTILE LAG LEAD FIRST_VALUE LAST_VALUE ABS CEILING FLOOR ROUND POWER SQRT SIGN ISNUMERIC ISDATE OBJECT_ID NEWID SCOPE_IDENTITY IDENT_CURRENT STR REVERSE YEAR MONTH DAY IIF CHOOSE HASHBYTES'.split(' '));

  let out = '';
  let i = 0;
  const len = code.length;

  while (i < len) {
    // -- line comment: colour to end of line
    if (code[i] === '-' && code[i+1] === '-') {
      let j = i;
      while (j < len && code[j] !== '\n') j++;
      out += `<span style="color:#6a9955">${esc(code.slice(i,j))}</span>`;
      i = j;
      continue;
    }
    // /* block comment */
    if (code[i] === '/' && code[i+1] === '*') {
      let j = i + 2;
      while (j < len - 1 && !(code[j] === '*' && code[j+1] === '/')) j++;
      j = Math.min(j + 2, len);
      out += `<span style="color:#6a9955">${esc(code.slice(i,j))}</span>`;
      i = j;
      continue;
    }
    // 'string literal' — handle '' escape
    if (code[i] === "'") {
      let j = i + 1;
      while (j < len) {
        if (code[j] === "'" && code[j+1] === "'") { j += 2; continue; }
        if (code[j] === "'") { j++; break; }
        j++;
      }
      out += `<span style="color:#ce9178">${esc(code.slice(i,j))}</span>`;
      i = j;
      continue;
    }
    // [bracket identifier]
    if (code[i] === '[') {
      let j = i + 1;
      while (j < len && code[j] !== ']') j++;
      j = Math.min(j + 1, len);
      out += `<span style="color:#9cdcfe">${esc(code.slice(i,j))}</span>`;
      i = j;
      continue;
    }
    // word — keyword, function, or plain identifier
    if (/[a-zA-Z_@#]/.test(code[i])) {
      let j = i;
      while (j < len && /[\w@#]/.test(code[j])) j++;
      const word = code.slice(i, j);
      const up   = word.toUpperCase();
      if (KW.has(up))
        out += `<span style="color:#569cd6;font-weight:600">${esc(word)}</span>`;
      else if (FN.has(up))
        out += `<span style="color:#dcdcaa">${esc(word)}</span>`;
      else
        out += `<span style="color:#d4d4d4">${esc(word)}</span>`;
      i = j;
      continue;
    }
    // number literal
    if (/[0-9]/.test(code[i])) {
      let j = i;
      while (j < len && /[0-9.]/.test(code[j])) j++;
      out += `<span style="color:#b5cea8">${esc(code.slice(i,j))}</span>`;
      i = j;
      continue;
    }
    // everything else (operators, whitespace, punctuation) — plain
    out += esc(code[i]);
    i++;
  }
  return out;
}

// ===========================================================
// ===========================================================
// SQL EDITOR — contenteditable approach
//
// WHY: <textarea> is a native OS form-control. On Windows Chrome
// it renders text through a different internal path than any
// HTML element (<pre>, <div>). No amount of CSS matching can
// make an overlay pixel-perfect against it.
//
// FIX: Replace the textarea with a <div contenteditable>.
// The cursor lives in the SAME element as the highlighted text,
// so there is no overlay and no drift by definition.
// ===========================================================
const MONO = "'Consolas','Cascadia Code','Courier New',monospace";

// ── Cursor helpers ────────────────────────────────────────────
// Work in plain-text character offsets, which survive a full
// innerHTML replacement (our spans are all inline, no block els).
function getCaretOffset(root) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return 0;
  const range = sel.getRangeAt(0).cloneRange();
  range.selectNodeContents(root);
  range.setEnd(sel.getRangeAt(0).endContainer, sel.getRangeAt(0).endOffset);
  return range.toString().length;
}

function setCaretOffset(root, offset) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
  let charIdx = 0;
  let node;
  while ((node = walker.nextNode())) {
    const len = node.nodeValue.length;
    if (offset <= charIdx + len) {
      const range = document.createRange();
      range.setStart(node, offset - charIdx);
      range.collapse(true);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    }
    charIdx += len;
  }
  // fallback — end of content
  const range = document.createRange();
  range.selectNodeContents(root);
  range.collapse(false);
  window.getSelection().removeAllRanges();
  window.getSelection().addRange(range);
}

// ── Editor component ─────────────────────────────────────────
const Editor = ({ tabId, value, onChange, onExecute, onExecuteSelected, onNewTab, textareaRefs }) => {
  const divRef   = useRef(null);
  const lineRef  = useRef(null);

  // Refs for stable closure access inside native event handlers
  const onChangeRef          = useRef(onChange);
  const onExecuteRef         = useRef(onExecute);
  const onExecuteSelectedRef = useRef(onExecuteSelected);
  const onNewTabRef          = useRef(onNewTab);
  const valueRef             = useRef(value);
  useEffect(() => { onChangeRef.current = onChange; },                   [onChange]);
  useEffect(() => { onExecuteRef.current = onExecute; },                 [onExecute]);
  useEffect(() => { onExecuteSelectedRef.current = onExecuteSelected; }, [onExecuteSelected]);
  useEffect(() => { onNewTabRef.current = onNewTab; },                   [onNewTab]);
  useEffect(() => { valueRef.current = value; },                         [value]);

  const lineCount = useMemo(() => (value.match(/\n/g) || []).length + 1, [value]);

  // ── Undo / redo history ────────────────────────────────────
  // We re-render innerHTML on every keystroke, which destroys the
  // browser's native undo stack. So we keep our own: snapshots of
  // { text, caret }. pushHistory() is called *before* a mutation.
  const undoStack = useRef([]);   // [{ text, caret }]
  const redoStack = useRef([]);
  const lastSnapTime = useRef(0);

  const snapshot = useCallback(() => {
    const div = divRef.current;
    return { text: valueRef.current, caret: div ? getCaretOffset(div) : 0 };
  }, []);

  // Push current state onto the undo stack before changing it.
  // `coalesce` groups rapid typing into one undo step (like real editors):
  // consecutive char inserts within 400ms collapse into a single entry.
  const pushHistory = useCallback((coalesce = false) => {
    const now = Date.now();
    if (coalesce && now - lastSnapTime.current < 400 && undoStack.current.length) {
      lastSnapTime.current = now;
      redoStack.current = [];
      return; // fold into the previous snapshot
    }
    undoStack.current.push(snapshot());
    if (undoStack.current.length > 500) undoStack.current.shift();
    redoStack.current = [];
    lastSnapTime.current = now;
  }, [snapshot]);

  const applyState = useCallback((state) => {
    const div = divRef.current;
    if (!div || !state) return;
    valueRef.current = state.text;
    div.innerHTML = sqlHighlight(state.text) + '\u200B';
    setCaretOffset(div, Math.min(state.caret, state.text.length));
    onChangeRef.current(state.text);
    buildShim();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const doUndo = useCallback(() => {
    if (!undoStack.current.length) return;
    redoStack.current.push(snapshot());
    applyState(undoStack.current.pop());
    lastSnapTime.current = 0;
  }, [snapshot, applyState]);

  const doRedo = useCallback(() => {
    if (!redoStack.current.length) return;
    undoStack.current.push(snapshot());
    applyState(redoStack.current.pop());
    lastSnapTime.current = 0;
  }, [snapshot, applyState]);

  // Expose a "textarea-like" ref for the rest of the app
  // (handleExecute reads selectionStart/End from this shim)
  const taShimRef = useRef(null);
  const buildShim = useCallback(() => {
    const div = divRef.current;
    if (!div) return;
    const sel = window.getSelection();
    let ss = 0, se = 0;
    if (sel && sel.rangeCount) {
      const range = sel.getRangeAt(0).cloneRange();
      const pre = range.cloneRange();
      pre.selectNodeContents(div);
      pre.setEnd(range.startContainer, range.startOffset);
      ss = pre.toString().length;
      pre.setEnd(range.endContainer, range.endOffset);
      se = pre.toString().length;
    }
    if (!taShimRef.current) taShimRef.current = {};
    taShimRef.current.value          = valueRef.current;
    taShimRef.current.selectionStart = ss;
    taShimRef.current.selectionEnd   = se;
    textareaRefs.current[tabId]      = taShimRef.current;
  }, [tabId, textareaRefs]);

  // Re-render highlighted HTML when value changes externally
  // (tab switch, Clear button, execute-selected passthrough, etc.)
  useEffect(() => {
    const div = divRef.current;
    if (!div) return;
    // Compute what the current plain text IS in the div
    const current = div.innerText.replace(/\u200B/g, '');
    if (current === value) return; // user is typing — don't interfere
    // External change — re-render and place caret at end
    div.innerHTML = sqlHighlight(value) + '\u200B';
    setCaretOffset(div, value.length);
  }, [value]);

  // Mount — set initial content
  useEffect(() => {
    const div = divRef.current;
    if (!div) return;
    div.innerHTML = sqlHighlight(value) + '\u200B';
    textareaRefs.current[tabId] = taShimRef.current || {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Native handlers attached once ──────────────────────────
  useEffect(() => {
    const div = divRef.current;
    if (!div) return;

    // INPUT: user typed/deleted — extract plain text, re-highlight, restore caret
    const handleInput = () => {
      const offset = getCaretOffset(div);
      // innerText on a white-space:pre contenteditable gives us the correct
      // newlines from actual \n chars in text nodes (not from CSS layout).
      const plain = div.innerText.replace(/\u200B/g, '');
      valueRef.current = plain;
      div.innerHTML = sqlHighlight(plain) + '\u200B';
      setCaretOffset(div, offset);
      onChangeRef.current(plain);
      buildShim();
    };

    // KEYDOWN: intercept shortcuts BEFORE browser acts on them
    const handleKeyDown = (e) => {
      const div = divRef.current;
      if (!div) return;
      buildShim(); // refresh shim selection before checking

      // ── Undo / Redo (our own stack, native one is wiped on re-highlight) ──
      const isUndo = (e.ctrlKey || e.metaKey) && !e.shiftKey && (e.key === 'z' || e.key === 'Z');
      const isRedo = (e.ctrlKey || e.metaKey) && ((e.key === 'y' || e.key === 'Y') || (e.shiftKey && (e.key === 'z' || e.key === 'Z')));
      if (isRedo) { e.preventDefault(); doRedo(); return; }
      if (isUndo) { e.preventDefault(); doUndo(); return; }

      // Snapshot before a normal character insert or a delete so Ctrl+Z
      // has something to roll back to. Printable keys = length-1 key with
      // no ctrl/meta. Backspace/Delete also get a (non-coalesced) snapshot.
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        if (e.key.length === 1) pushHistory(true);          // typing — coalesce bursts
        else if (e.key === 'Backspace' || e.key === 'Delete') pushHistory(false);
      }

      // ── F5 → execute (selection if any, else all) ──────────────
      if (e.key === 'F5') {
        e.preventDefault(); e.stopPropagation();
        const shim = taShimRef.current;
        const sel = shim && shim.selectionStart !== shim.selectionEnd
          ? shim.value.substring(shim.selectionStart, shim.selectionEnd).trim()
          : null;
        onExecuteRef.current(sel);
        return;
      }
      // ── Ctrl+Enter → execute ───────────────────────────────────
      if (e.ctrlKey && !e.shiftKey && !e.altKey && e.key === 'Enter') {
        e.preventDefault();
        const shim = taShimRef.current;
        const sel = shim && shim.selectionStart !== shim.selectionEnd
          ? shim.value.substring(shim.selectionStart, shim.selectionEnd).trim()
          : null;
        onExecuteRef.current(sel);
        return;
      }
      // ── Ctrl+Shift+Enter → execute selected ───────────────────
      if (e.ctrlKey && e.shiftKey && e.key === 'Enter') {
        e.preventDefault();
        const shim = taShimRef.current;
        const sel = shim && shim.selectionStart !== shim.selectionEnd
          ? shim.value.substring(shim.selectionStart, shim.selectionEnd).trim()
          : null;
        onExecuteSelectedRef.current(sel);
        return;
      }
      // ── Alt+T → new tab ────────────────────────────────────────
      if (e.altKey && !e.ctrlKey && (e.key === 't' || e.key === 'T')) {
        e.preventDefault(); onNewTabRef.current(); return;
      }
      // ── Escape ─────────────────────────────────────────────────
      if (e.key === 'Escape') { div.blur(); return; }

      // ── Enter → insert literal \n (prevent browser inserting <div>/<br>) ─
      if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey) {
        e.preventDefault();
        const offset = getCaretOffset(div);
        const v = valueRef.current;
        const newVal = v.slice(0, offset) + '\n' + v.slice(offset);
        valueRef.current = newVal;
        div.innerHTML = sqlHighlight(newVal) + '\u200B';
        setCaretOffset(div, offset + 1);
        onChangeRef.current(newVal);
        buildShim();
        return;
      }

      // ── Tab → 2 spaces ─────────────────────────────────────────
      if (e.key === 'Tab') {
        e.preventDefault();
        const offset = getCaretOffset(div);
        const v = valueRef.current;
        const newVal = v.slice(0, offset) + '  ' + v.slice(offset);
        valueRef.current = newVal;
        div.innerHTML = sqlHighlight(newVal) + '\u200B';
        setCaretOffset(div, offset + 2);
        onChangeRef.current(newVal);
        buildShim();
        return;
      }

      // ── Ctrl+/ → toggle line comment ──────────────────────────
      if (e.ctrlKey && (e.key === '/' || e.code === 'Slash')) {
        e.preventDefault();
        pushHistory();
        buildShim();
        const shim = taShimRef.current;
        const v = valueRef.current;
        const ss = shim ? shim.selectionStart : getCaretOffset(div);
        const se = shim ? shim.selectionEnd   : ss;
        const lines = v.split('\n');
        const startLine = v.slice(0, ss).split('\n').length - 1;
        const endLine   = v.slice(0, se).split('\n').length - 1;
        const allCommented = lines.slice(startLine, endLine + 1)
          .every(l => l.trimStart().startsWith('--'));
        const newLines = lines.map((l, i) => {
          if (i < startLine || i > endLine) return l;
          return allCommented ? l.replace(/^(\s*)--\s?/, '$1') : l.replace(/^(\s*)/, '$1-- ');
        });
        const newVal = newLines.join('\n');
        const delta  = allCommented ? -3 : 3;
        valueRef.current = newVal;
        div.innerHTML = sqlHighlight(newVal) + '\u200B';
        setCaretOffset(div, Math.max(0, ss + delta));
        onChangeRef.current(newVal);
        buildShim();
        return;
      }

      // ── Single quote → insert exactly one ' (no auto-pairing) ──
      if (e.key === "'") {
        e.preventDefault();
        pushHistory();
        const shim = taShimRef.current;
        const ss = shim ? shim.selectionStart : getCaretOffset(div);
        const se = shim ? shim.selectionEnd   : ss;
        const v = valueRef.current;
        const newVal = v.slice(0, ss) + "'" + v.slice(se);
        valueRef.current = newVal;
        div.innerHTML = sqlHighlight(newVal) + '\u200B';
        setCaretOffset(div, ss + 1);
        onChangeRef.current(newVal);
        buildShim();
        return;
      }

      // ── Auto-close ( ──────────────────────────────────────────
      if (e.key === '(') {
        e.preventDefault();
        pushHistory();
        const offset = getCaretOffset(div);
        const v = valueRef.current;
        const newVal = v.slice(0, offset) + '()' + v.slice(offset);
        valueRef.current = newVal;
        div.innerHTML = sqlHighlight(newVal) + '\u200B';
        setCaretOffset(div, offset + 1);
        onChangeRef.current(newVal);
        buildShim();
        return;
      }
      if (e.key === ')') {
        const offset = getCaretOffset(div);
        if (valueRef.current[offset] === ')') {
          e.preventDefault(); setCaretOffset(div, offset + 1); return;
        }
      }
    };

    // PASTE: strip HTML, insert plain text at cursor (replace selection if any)
    const handlePaste = (e) => {
      e.preventDefault();
      pushHistory();
      const text = e.clipboardData.getData('text/plain');
      const v = valueRef.current;
      // Get selection range from the shim (start + end)
      const shim = taShimRef.current;
      const ss = shim ? shim.selectionStart : getCaretOffset(div);
      const se = shim ? shim.selectionEnd   : ss;
      // Replace the selected range (or insert at caret if no selection)
      const newVal = v.slice(0, ss) + text + v.slice(se);
      valueRef.current = newVal;
      div.innerHTML = sqlHighlight(newVal) + '\u200B';
      setCaretOffset(div, ss + text.length);
      onChangeRef.current(newVal);
      buildShim();
    };

    // SCROLL: sync line numbers
    const handleScroll = () => {
      if (lineRef.current) lineRef.current.scrollTop = div.scrollTop;
    };

    // SELECTIONCHANGE: keep shim in sync for execute-selected
    const handleSelChange = () => {
      if (document.activeElement === div) buildShim();
    };

    div.addEventListener('input',  handleInput);
    div.addEventListener('keydown', handleKeyDown);
    div.addEventListener('paste',  handlePaste);
    div.addEventListener('scroll', handleScroll);
    document.addEventListener('selectionchange', handleSelChange);

    return () => {
      div.removeEventListener('input',  handleInput);
      div.removeEventListener('keydown', handleKeyDown);
      div.removeEventListener('paste',  handlePaste);
      div.removeEventListener('scroll', handleScroll);
      document.removeEventListener('selectionchange', handleSelChange);
    };
  }, [buildShim, pushHistory, doUndo, doRedo]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ flex: 1, display: 'flex', background: '#1e1e1e', overflow: 'hidden', minHeight: 0 }}>

      {/* Line numbers */}
      <div
        ref={lineRef}
        style={{
          background: '#1e1e1e', color: '#495363',
          userSelect: 'none', textAlign: 'right',
          fontFamily: MONO, fontSize: '13px', lineHeight: '21.45px',
          padding: '10px 10px 10px 8px',
          minWidth: 48, flexShrink: 0,
          overflowY: 'hidden', overflowX: 'hidden',
          borderRight: '1px solid #2d2d2d', boxSizing: 'border-box',
        }}
      >
        {Array.from({ length: lineCount }, (_, i) => (
          <div key={i} style={{ height: '21.45px' }}>{i + 1}</div>
        ))}
      </div>

      {/* contenteditable editor — ONE element, no overlay, no drift */}
      <div
        ref={divRef}
        contentEditable="true"
        suppressContentEditableWarning
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        data-placeholder={"-- Write SQL here\n-- F5 / Ctrl+Enter: Execute All   Ctrl+Shift+Enter: Execute Selected   Ctrl+/: Toggle Comment   Alt+T: New Tab"}
        style={{
          flex: 1,
          fontFamily: MONO,
          fontSize: '13px',
          lineHeight: '21.45px',
          padding: '10px 14px',
          color: '#d4d4d4',
          background: '#1e1e1e',
          border: 'none',
          outline: 'none',
          overflowY: 'auto',
          overflowX: 'auto',
          whiteSpace: 'pre',
          tabSize: 2,
          MozTabSize: 2,
          boxSizing: 'border-box',
          caretColor: '#aeafad',
          wordBreak: 'normal',
        }}
      />
    </div>
  );
};

// RESULTS PANE (Results tab + Messages tab)
// ===========================================================
const ResultsPane = ({ tab, activePane, setActivePane, onExport }) => {
  const [sectionHeights, setSectionHeights] = useState([]);

  useEffect(() => {
    if (tab.results.length > 0) setSectionHeights(tab.results.map(() => 100 / tab.results.length));
  }, [tab.results.length]);

  const handleResultDividerDrag = useCallback((idx, delta) => {
    setSectionHeights(prev => {
      const h = [...prev], min = 8;
      let a = h[idx] + delta * 0.35, b = h[idx + 1] - delta * 0.35;
      const tot = h[idx] + h[idx + 1];
      if (a < min) { a = min; b = tot - min; }
      if (b < min) { b = min; a = tot - min; }
      h[idx] = a; h[idx + 1] = b;
      return h;
    });
  }, []);

  const hasResults = tab.results.length > 0;
  const hasErrors = tab.results.some(r => !r.success) || tab.messages.some(m => m.type === 'error');

  const tabStyle = active => ({
    padding: '5px 18px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    border: 'none', background: 'transparent',
    color: active ? '#cccccc' : '#666666',
    borderBottom: active ? '2px solid #0078d4' : '2px solid transparent',
    transition: 'all 0.12s',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#1e1e1e' }}>

      {/* Pane tab strip */}
      <div style={{ display: 'flex', alignItems: 'center', background: '#252526', borderBottom: '1px solid #1a1a1a', flexShrink: 0 }}>
        <button style={tabStyle(activePane === 'results')} onClick={() => setActivePane('results')}>
          Results
          {hasResults && <span style={{ marginLeft: 5, padding: '1px 6px', background: '#1a3356', color: '#7bb4e8', borderRadius: 99, fontSize: 10, fontWeight: 700 }}>{tab.results.length}</span>}
        </button>
        <button style={tabStyle(activePane === 'messages')} onClick={() => setActivePane('messages')}>
          Messages
          {hasErrors && <span style={{ marginLeft: 5, padding: '1px 6px', background: '#3a1010', color: '#f87171', borderRadius: 99, fontSize: 10, fontWeight: 700 }}>!</span>}
        </button>
        {tab.stats && (
          <span style={{ marginLeft: 'auto', paddingRight: 14, fontSize: 11, color: tab.stats.failed ? '#f87171' : '#4ade80' }}>
            {tab.stats.failed ? `${tab.stats.failed} error(s)` : `${tab.stats.rows.toLocaleString()} row(s) · ${tab.stats.time}s`}
          </span>
        )}
      </div>

      {/* Content area */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>

        {/* RESULTS TAB */}
        {activePane === 'results' && (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            {tab.loading && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 14, color: '#888' }}>
                <div style={{ width: 30, height: 30, border: '3px solid #333', borderTopColor: '#0078d4', borderRadius: '50%', animation: 'qs-spin 0.7s linear infinite' }} />
                <span style={{ fontSize: 13 }}>Executing queries…</span>
              </div>
            )}
            {!tab.loading && !hasResults && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#3d4451', gap: 10 }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" />
                </svg>
                <span style={{ fontSize: 13 }}>Results will appear here</span>
                <span style={{ fontSize: 11, color: '#2d3441' }}>Press F5 or click Execute to run your query</span>
              </div>
            )}
            {!tab.loading && hasResults && tab.results.map((res, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && <ResultDivider index={idx - 1} onDrag={handleResultDividerDrag} />}
                <ResultSection result={res} height={sectionHeights[idx] || 50} onExport={onExport} />
              </React.Fragment>
            ))}
          </div>
        )}

        {/* MESSAGES TAB */}
        {activePane === 'messages' && (
          <div style={{ height: '100%', overflowY: 'auto', padding: '12px 16px', fontFamily: MONO, fontSize: 12, lineHeight: 1.9 }}>
            {tab.messages.length === 0 ? (
              <span style={{ color: '#3d4451' }}>No messages</span>
            ) : tab.messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', color: m.type === 'error' ? '#f87171' : m.type === 'warn' ? '#fbbf24' : '#6a9955' }}>
                <span style={{ opacity: 0.7, flexShrink: 0 }}>{m.type === 'error' ? '✗' : m.type === 'warn' ? '⚠' : '✓'}</span>
                <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ===========================================================
// RESULT SECTION
// ===========================================================
const ResultSection = ({ result, height, onExport }) => {
  const { success, data, error, executionTime, tableName } = result;
  const [colWidths, setColWidths] = useState([]);
  const [sortCol, setSortCol] = useState(null); // { idx, dir }
  const [lastCopied, setLastCopied] = useState(null);

  const rowCount = data?.rowCount ?? 0;
  const title = tableName || (success ? 'Result' : 'Error');

  useEffect(() => {
    if (success && data?.columns) setColWidths(Array(data.columns.length).fill(150));
  }, [success, data]);

  const handleColResize = useCallback((i, delta) => {
    setColWidths(prev => { const w = [...prev]; w[i] = Math.max(60, Math.min(700, w[i] + delta)); return w; });
  }, []);

  const handleCellClick = useCallback((val) => {
    const text = val == null ? '' : String(val);
    navigator.clipboard?.writeText(text).then(() => {
      setLastCopied(text);
      setTimeout(() => setLastCopied(null), 900);
    }).catch(() => { });
  }, []);

  const toggleSort = idx => setSortCol(prev =>
    prev?.idx === idx ? (prev.dir === 'asc' ? { idx, dir: 'desc' } : null) : { idx, dir: 'asc' }
  );

  const displayRows = useMemo(() => {
    if (!success || !data || !sortCol) return data?.rows || [];
    return [...data.rows].sort((a, b) => {
      const av = a[sortCol.idx], bv = b[sortCol.idx];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortCol.dir === 'asc' ? cmp : -cmp;
    });
  }, [success, data, sortCol]);

  return (
    <div style={{ height: `${height}%`, minHeight: 100, display: 'flex', flexDirection: 'column', background: '#1e1e1e', borderBottom: '1px solid #252526' }}>

      {/* Section header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 12px', background: '#252526', borderBottom: '1px solid #1a1a1a', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 12, color: '#aaaaaa' }}>{title}</span>
          {success && <span style={{ padding: '1px 8px', borderRadius: 99, background: '#0d2a1a', color: '#4ade80', fontSize: 10, fontWeight: 700, border: '1px solid #1a4a2a' }}>{rowCount.toLocaleString()} row{rowCount !== 1 ? 's' : ''}</span>}
          <span style={{ fontSize: 11, color: success ? '#4ade80' : '#f87171', fontWeight: 500 }}>{success ? '✓' : '✗'} {executionTime}s</span>
          {sortCol && <span style={{ fontSize: 10, color: '#7bb4e8' }}>↕ sorted by col {sortCol.idx + 1} ({sortCol.dir})</span>}
          {lastCopied !== null && <span style={{ fontSize: 10, color: '#fbbf24' }}>✓ Copied</span>}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {sortCol && (
            <button onClick={() => setSortCol(null)} style={{ padding: '2px 8px', fontSize: 10, background: '#2a2d2e', color: '#888', border: '1px solid #444', borderRadius: 3, cursor: 'pointer' }}>
              Clear Sort
            </button>
          )}
          {success && rowCount > 0 && (
            <button
              onClick={() => onExport(data.columns, displayRows, `${title}_${new Date().toISOString().slice(0, 10)}.xlsx`)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 4, border: '1px solid #1a4a2a', background: '#0d2a1a', color: '#4ade80', fontSize: 11, fontWeight: 600, cursor: 'pointer', transition: 'all 0.12s' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#0e7a0e'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#0d2a1a'; e.currentTarget.style.color = '#4ade80'; }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
              Export Excel
            </button>
          )}
        </div>
      </div>

      {/* Table / error / empty */}
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {!success ? (
          <div style={{ margin: 12, padding: '10px 14px', background: '#2a1010', border: '1px solid #4a2020', borderRadius: 6, color: '#f87171', fontSize: 12, fontFamily: MONO, lineHeight: 1.7 }}>
            <div style={{ fontWeight: 700, color: '#fc8181', marginBottom: 4 }}>Error</div>
            {error}
          </div>
        ) : rowCount === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#555', fontSize: 12 }}>
            (0 rows affected)
          </div>
        ) : (
          <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', minWidth: '100%', fontSize: 12 }}>
            <thead>
              <tr>
                {/* Row # */}
                <th style={{ position: 'sticky', top: 0, left: 0, zIndex: 11, width: 48, minWidth: 48, textAlign: 'center', padding: '6px 8px', background: '#252526', borderBottom: '1px solid #3c3c3c', borderRight: '1px solid #2d2d2d', color: '#555', fontWeight: 600, fontSize: 10, userSelect: 'none' }}>#</th>
                {data.columns.map((col, i) => (
                  <th key={i}
                    className="qs-hdr"
                    onClick={() => toggleSort(i)}
                    title={`Click to sort by "${col}"`}
                    style={{ position: 'relative', width: colWidths[i] || 150, minWidth: colWidths[i] || 150, maxWidth: colWidths[i] || 150, padding: '6px 12px 6px 10px', background: '#252526', borderBottom: '1px solid #3c3c3c', borderRight: '1px solid #2d2d2d', color: '#9cdcfe', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', userSelect: 'none', transition: 'background 0.1s' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {col}
                      {sortCol?.idx === i && <span style={{ fontSize: 10, color: '#7bb4e8' }}>{sortCol.dir === 'asc' ? '↑' : '↓'}</span>}
                    </span>
                    <ColResizer colIndex={i} onResize={handleColResize} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row, ri) => (
                <tr key={ri} className="qs-row" style={{ background: ri % 2 === 0 ? '#1e1e1e' : '#252526' }}>
                  <td style={{ position: 'sticky', left: 0, zIndex: 1, width: 48, textAlign: 'center', padding: '5px 8px', color: '#444', fontSize: 10, fontWeight: 500, background: 'inherit', borderRight: '1px solid #2d2d2d', borderBottom: '1px solid #252526' }}>{ri + 1}</td>
                  {row.map((cell, ci) => (
                    <td key={ci}
                      onClick={() => handleCellClick(cell)}
                      title={cell != null ? String(cell) : 'NULL — click to copy empty string'}
                      style={{ padding: '5px 12px', width: colWidths[ci] || 150, maxWidth: colWidths[ci] || 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', borderRight: '1px solid #2a2a2a', borderBottom: '1px solid #252526', color: cell == null ? '#3d4451' : '#d4d4d4', fontStyle: cell == null ? 'italic' : 'normal', fontFamily: (typeof cell === 'number' || cell === null) ? MONO : 'inherit', cursor: 'pointer', fontSize: 12, transition: 'background 0.15s' }}
                    >
                      {cell == null ? 'NULL' : String(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ===========================================================
// STATUS BAR  (bottom, SSMS-style blue bar)
// ===========================================================
const StatusBar = ({ tab, domain, textareaRefs }) => {
  const [cursor, setCursor] = useState({ line: 1, col: 1, sel: 0 });

  useEffect(() => {
    // The editor is now a contenteditable div. We get cursor position
    // from the shim object (plain JS object, not a DOM element) which is
    // updated by the Editor's selectionchange listener. We poll via the
    // document-level selectionchange event — no DOM listeners on the shim.
    const update = () => {
      const shim = textareaRefs.current[tab.id];
      if (!shim || typeof shim.selectionStart !== 'number') return;
      const { selectionStart: ss, selectionEnd: se, value: v } = shim;
      if (!v) return;
      const before = v.slice(0, ss);
      const line = (before.match(/\n/g) || []).length + 1;
      const col  = ss - before.lastIndexOf('\n');
      setCursor({ line, col, sel: Math.abs(se - ss) });
    };

    document.addEventListener('selectionchange', update);
    return () => document.removeEventListener('selectionchange', update);
  }, [tab.id, textareaRefs]);

  const totalRows = tab.results.reduce((s, r) => s + (r.success && r.data?.rowCount ? r.data.rowCount : 0), 0);
  const connLabel = domain || sessionStorage.getItem('selectedDomain') || 'Not Connected';

  return (
    <div style={{ display: 'flex', alignItems: 'center', background: '#007acc', color: '#fff', padding: '2px 14px', fontSize: 11, flexShrink: 0, gap: 14, userSelect: 'none', minHeight: 22 }}>
      {/* Connection */}
      <span style={{ fontWeight: 700, letterSpacing: '0.3px' }}>{connLabel}</span>
      <span style={{ opacity: 0.5 }}>-</span>

      {/* Cursor position */}
      <span>Ln {cursor.line}  Col {cursor.col}</span>
      {cursor.sel > 0 && <span style={{ opacity: 0.8 }}>({cursor.sel} selected)</span>}

      {/* Execution stats */}
      {tab.stats && (
        <>
          <span style={{ opacity: 0.5 }}>-</span>
          <span style={{ color: tab.stats.failed ? '#fca5a5' : '#bbf7d0' }}>
            {tab.stats.statements} stmt · {totalRows.toLocaleString()} rows · {tab.stats.time}s
            {tab.stats.failed > 0 && ` · ${tab.stats.failed} error(s)`}
          </span>
        </>
      )}

      {/* Loading indicator */}
      {tab.loading && (
        <>
          <span style={{ opacity: 0.5 }}>-</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, border: '1.5px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'qs-spin 0.7s linear infinite' }} />
            Executing…
          </span>
        </>
      )}

      <div style={{ flex: 1 }} />
      <span style={{ opacity: 0.6 }}>SQL Server  ·  UTF-8  ·  CRLF</span>
    </div>
  );
};

// ===========================================================
// RESULT DIVIDER (drag to resize result sections)
// ===========================================================
const ResultDivider = ({ index, onDrag }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    let drag = false, sY = 0;
    const start = e => { if (e.pointerType === 'mouse' && e.button !== 0) return; e.preventDefault(); drag = true; sY = e.clientY; document.body.style.userSelect = 'none'; document.body.style.cursor = 'row-resize'; };
    const move = e => { if (!drag) return; onDrag(index, e.clientY - sY); sY = e.clientY; };
    const stop = () => { drag = false; document.body.style.userSelect = ''; document.body.style.cursor = ''; };
    el.addEventListener('pointerdown', start);
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', stop);
    return () => { el.removeEventListener('pointerdown', start); window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', stop); };
  }, [index, onDrag]);
  return (
    <div ref={ref}
      style={{ height: 4, background: '#2a2a2a', cursor: 'row-resize', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.1s' }}
      onMouseEnter={e => e.currentTarget.style.background = '#0078d4'}
      onMouseLeave={e => e.currentTarget.style.background = '#2a2a2a'}
    >
      <div style={{ width: 48, height: 2, background: '#444', borderRadius: 99 }} />
    </div>
  );
};

// ===========================================================
// COLUMN RESIZER
// ===========================================================
const ColResizer = ({ colIndex, onResize }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    let drag = false, sX = 0;
    const start = e => { if (e.button !== 0) return; e.preventDefault(); e.stopPropagation(); drag = true; sX = e.clientX; document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none'; };
    const move = e => { if (!drag) return; onResize(colIndex, (e.clientX - sX) * 1.2); sX = e.clientX; };
    const stop = () => { drag = false; document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    el.addEventListener('mousedown', start);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', stop);
    return () => { el.removeEventListener('mousedown', start); window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', stop); };
  }, [colIndex, onResize]);
  return (
    <div ref={ref}
      style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 5, cursor: 'col-resize', zIndex: 5, borderRight: '2px solid transparent', transition: 'border-color 0.1s' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = '#0078d4'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
    />
  );
};

export default QueryAnalyzer;